import type {
    Brand,
    PrePaidCardAccountType,
    PrePaidCardTransportationCardType,
    카드타입코드,
} from '$share/constants/card'
import type {RegistrationStatus, Asset} from '$share/types/asset'
import type {CommonDate} from '$share/types/common'
import type {AssetOrg, Organization} from '$share/types/organization'

export type CardOrg = Pick<Organization, 'orgCode' | 'name' | 'displayName' | 'displayName2' | 'industry' | 'odr'>

export interface UniCardCommon {
    name: string
    nickname: null | string
    num: string
    imageUrl: null | string
    enabled: boolean
}

export type MyDataCard = Readonly<{
    id: Asset['id'] // Asset ID
    org: AssetOrg
    seqno: string // 카드식별자 (card_id 값)
    name: string // 카드명
    nickname: string | null // 카드별명
    enabled: boolean // 노출상태
    status: RegistrationStatus // 자산 등록상태
    num: string // 카드번호
    consentMerchantName: boolean // 가맹점명 전송요구 여부 (기관단위)
    type: 카드타입코드 // 카드타입
    cardUser: 1 | 2 // 1:본인, 2:가족
    consent: boolean // 마이데이터 전송요구 여부
    issueDate: CommonDate // 발급일자 (YYYY-MM-DD)
    transPayable: boolean // 교통기능 여부
    cashCard: boolean // 현금카드기능 여부
    linkedBankCode: string | null // 결제은행
    linkedAccNum: string | null // 결제계좌번호
    cardBrand: Brand // 카드브랜드
    annualFee: number // 연회비
    imageUrl: string | null // (nullable)카드이미지URL. url 뒤에 '?type=a114*171' 으로 섬네일 로딩. 섬네일 type: a114*171
    monthlyRecord: number | null // 카드 실적
    createAt: string // 생성시각
    updateAt: string // 수정시각
}>

export type MdCard = Pick<
    MyDataCard,
    | 'id'
    | 'org'
    | 'seqno'
    | 'status'
    | 'consentMerchantName'
    | 'type'
    | 'cardUser'
    | 'consent'
    | 'issueDate'
    | 'transPayable'
    | 'cashCard'
    | 'linkedBankCode'
    | 'cardBrand'
    | 'annualFee'
    | 'createAt'
    | 'updateAt'
>

export interface NfCard {
    cardId: string
    productCode: string
    accountNo: string
    accountInstitution: string // 계좌 기관
    accountType: PrePaidCardAccountType
    transportationCard: PrePaidCardTransportationCardType
    /**
     * 보유카드상태
     * (in-use: 일시정지, suspension: 분실신고, loss: 배송반송 혹은 폐기, discard-pending: '해지' , discard: '해지(내부용)', expiration: '만료', use-pending: '사용대기')
     */
    lifecycle: 'in-use' | 'suspension' | 'loss' | 'discard-pending' | 'discard' | 'expiration' | 'use-pending'
    cardScheme: string
    dcc: boolean // 해외 원화결제 허용 여부
    overseasDenied: boolean // 해외결제 차단 여부
    registered: boolean // 머니카드 등록여부
    /**
     * 배송상태정보
     * registered == false 이면 노출 (ready-to-ship: 발송준비, shipped: 발송완료, delivered: 배송완료, returned: 반송, disposed: 폐기)
     */
    deliveryStatus: 'ready-to-ship' | 'shipped' | 'delivered' | 'returned' | 'disposed'
    issuedDate: string // 발행일 (ex. "2024-08-09")
}

export interface UniMyDataCard extends UniCardCommon {
    mdCard: MdCard
}

export interface UniPrepaidCard extends UniCardCommon {
    nfCard: NfCard
}

export type UniCard = UniMyDataCard | UniPrepaidCard

export const isUniMyDataCard = (uniCard: UniCard): uniCard is UniMyDataCard => {
    return 'mdCard' in uniCard && uniCard.mdCard !== null
}

export const isUniPrepaidCard = (uniCard: UniCard): uniCard is UniPrepaidCard => {
    return 'nfCard' in uniCard && uniCard.nfCard !== null
}
